// Local-first landmark search — pure JS, no deps.
//
// Used by the mobile + web search-bar handlers (SearchScreen.js et al,
// and GoogleMapsAutoComplete.js) BEFORE calling Google Places. The
// decision tree there is:
//
//   results = searchLocal(query, gps, 8);
//   if (results.length >= 3) → render local, skip Google
//   if (1-2 hits)            → render local + call Google in parallel, merge
//   if (0 hits)              → call Google as today
//
// Combined with a 30-50k-entry bundled landmarks.json (OSM-sourced)
// this serves the vast majority of Addis autocomplete queries
// **without any network call**.
//
// Why not fuse.js: typical Addis queries are prefix/substring matches
// ("bole", "edna mall", "hilton"). Levenshtein-style fuzzy distance
// doesn't help much (English typos are rare; Amharic transliteration
// variance needs phonetic matching, which fuse.js doesn't do either).
// A custom tokenized matcher is faster (<10ms over 50k entries), has
// zero install footprint, and we can tune the scoring for our domain
// (GPS distance, category boosts) inline.

// Normalize a string for matching: lowercase, strip diacritics, strip
// non-alphanumerics → array of word tokens. Empty input returns [].
const _tokenize = (s) => {
    if (!s || typeof s !== 'string') return [];
    return s
        .normalize('NFD')                      // split accents into combining marks
        .replace(/[̀-ͯ]/g, '')      // drop the combining marks
        .toLowerCase()
        .replace(/[^a-z0-9ሀ-፿\s]/g, ' ') // keep ASCII + Ethiopic block
        .split(/\s+/)
        .filter(Boolean);
};

// Haversine distance in km. Inlined here (rather than importing from
// GeoFunctions) because we run this in a hot path — saves a require
// resolution per call. Mirrors the formula in GeoFunctions.js exactly.
const _haversineKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2))
        * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Score a single landmark against a query token array. Returns 0 if
// any meaningful query token doesn't match the landmark — see below
// for why this strictness matters. Higher = better.
//
// Per-token scoring (against any name/area/category token in landmark):
//   • landmark token === query token       → 1.0   ("bole" === "bole")
//   • landmark token startsWith query     → 0.85  ("bole".startsWith("bol"))
//   • query startsWith landmark token     → 0.65  ("addisababa".startsWith("addis"))
//   • landmark token includes query token → 0.45  ("medhanealem".includes("alem"))
//                                                  (was 0.55 — tightened to
//                                                   suppress noisy substring
//                                                   matches like "park" in
//                                                   "Parker Avenue")
//   • Else                                → 0
//
// ALL-MUST-MATCH rule for multi-word queries:
//   If ANY meaningful query token (length ≥ 2) scores 0, the WHOLE
//   landmark is rejected. Without this rule, "shalla park" averages
//   high against any landmark named "X Park" (the "park" token scores
//   1.0, dragging the average above the 0.4 threshold even though
//   "shalla" matched nothing). The user then sees a dozen irrelevant
//   parks AND we skip the Google fallback because we hit the ≥3 mark.
//   With the rule, "shalla park" only matches landmarks that contain
//   BOTH "shalla" AND "park" — and if none exist locally, the call
//   site correctly falls through to Google Places.
//
// Single-char tokens ("a", "&", etc.) are ignored as connectors —
// they'd otherwise reject every landmark since most don't contain
// single letters as separate tokens.
const _textScore = (queryTokens, landmarkTokens) => {
    if (queryTokens.length === 0 || landmarkTokens.length === 0) return 0;
    const significantTokens = queryTokens.filter((q) => q.length >= 2);
    if (significantTokens.length === 0) return 0;

    let sum = 0;
    for (const q of significantTokens) {
        let best = 0;
        for (const l of landmarkTokens) {
            let s = 0;
            if (l === q) s = 1.0;
            else if (l.startsWith(q)) s = 0.85;
            else if (q.length >= 4 && q.startsWith(l)) s = 0.65;
            else if (q.length >= 3 && l.includes(q)) s = 0.45;
            if (s > best) best = s;
            if (best === 1.0) break; // can't beat exact
        }
        // STRICT: any significant token with zero match rejects the
        // landmark. See the comment block above for why.
        if (best === 0) return 0;
        sum += best;
    }
    return sum / significantTokens.length;
};

// Build a search index — call once per landmark dataset change (caller
// memoizes). Pre-tokenizes the searchable text fields so the hot-path
// `searchLocal` doesn't re-tokenize on every keystroke.
//
// landmarks: array of {id, name, area?, category?, lat, lng, ...}
// Returns: array of {raw, tokens} where raw is the original record
// and tokens is the pre-computed match set.
export const buildLandmarkIndex = (landmarks) => {
    if (!Array.isArray(landmarks)) return [];
    const out = [];
    for (const lm of landmarks) {
        if (!lm || typeof lm.lat !== 'number' || typeof lm.lng !== 'number') continue;
        // Combine name + area + category into the searchable token set.
        // Category as a token means "fuel" matches all gas stations.
        const tokens = _tokenize(
            (lm.name || '') + ' ' + (lm.area || '') + ' ' + (lm.category || '')
        );
        if (tokens.length === 0) continue;
        out.push({ raw: lm, tokens });
    }
    return out;
};

// Main entry point. Returns the top `limit` matching landmarks for
// the query, biased by GPS proximity when `gps` is provided.
//
// Parameters:
//   query  — user input string
//   index  — pre-built index from buildLandmarkIndex()
//   gps    — { lat, lng } or null — current user location for proximity bias
//   limit  — max results to return (default 8 — matches typical
//            Places autocomplete dropdown length)
//
// Scoring:
//   final = textScore × distanceWeight
//   where distanceWeight = 1 / (1 + km / 10)  (10 km = half weight,
//   20 km = third weight). Tuned for Addis where the entire city fits
//   in ~25 km so distant-but-text-perfect hits still rank well.
//   When no GPS is provided, distanceWeight = 1 (pure text scoring).
//
// Returns array of { ...landmark, _score } sorted descending. Drops
// any result with textScore < 0.4 (avoids noisy substring matches
// like "park" matching "Parker St" 30km away).
export const searchLocal = (query, index, gps, limit = 8) => {
    const queryTokens = _tokenize(query);
    if (queryTokens.length === 0 || !Array.isArray(index) || index.length === 0) {
        return [];
    }
    // 0.5 threshold means a substring-only hit (e.g. "park" inside
    // "Parker Avenue") at 0.45 won't pass on its own — only real
    // prefix/exact matches do. Multi-word queries that pass the
    // all-must-match rule in _textScore have an average score floor
    // of 0.45 too, so this also nudges weak multi-word matches
    // (where every token is substring-only) out of the result set
    // and lets Google take the query instead.
    const minTextScore = 0.5;
    const useGps = gps && typeof gps.lat === 'number' && typeof gps.lng === 'number';

    const candidates = [];
    for (let i = 0; i < index.length; i++) {
        const entry = index[i];
        const textScore = _textScore(queryTokens, entry.tokens);
        if (textScore < minTextScore) continue;

        let finalScore = textScore;
        if (useGps) {
            const km = _haversineKm(gps.lat, gps.lng, entry.raw.lat, entry.raw.lng);
            const distWeight = 1 / (1 + km / 10);
            finalScore = textScore * distWeight;
            // Also stash distance on the result so the UI can show
            // "X km away" if it wants — useful UX flourish.
            candidates.push(Object.assign({}, entry.raw, {
                _score: finalScore,
                _textScore: textScore,
                _distanceKm: km,
            }));
        } else {
            candidates.push(Object.assign({}, entry.raw, {
                _score: finalScore,
                _textScore: textScore,
            }));
        }
    }

    candidates.sort((a, b) => b._score - a._score);
    return candidates.slice(0, limit);
};

// Convenience adapter — converts a local landmark into the shape the
// existing Google Places autocomplete UI expects from
// `fetchPlacesAutocomplete`: { description, place_id, ... }. Callers
// merge local + Google result arrays before rendering, so they need to
// look alike.
//
// The synthetic place_id is prefixed with `local:` so the selection
// handler can detect a local hit and skip the Place Details call (the
// landmark already carries lat/lng).
export const landmarkToAutocompleteShape = (landmark) => {
    if (!landmark) return null;
    return {
        description: landmark.area
            ? `${landmark.name} — ${landmark.area}`
            : landmark.name,
        place_id: 'local:' + landmark.id,
        // Keep originals so the selection handler can pull lat/lng
        // without a round-trip.
        _local: true,
        _lat: landmark.lat,
        _lng: landmark.lng,
        _name: landmark.name,
        _category: landmark.category,
    };
};

// Test helper — exported for unit tests, internal to the module
// otherwise. Lets tests assert tokenization without poking private
// state.
export const __internalTokenize = _tokenize;
